import { useEffect, useState } from 'react';
import { Star, MessageSquare } from 'lucide-react';
import supabase from '../lib/supabase';
import { PageHeader, Section, EmptyState, StatCard, Badge } from '../components/ui';

type Review = {
  id: string;
  rating: number;
  comment: string | null;
  created_at: string;
  profiles: { full_name: string | null } | null;
  coach_bookings: {
    id: string;
    lesson_type: string;
    environment: string;
    scheduled_at: string;
  } | null;
};

export default function CoachReviews({ coachId }: { coachId: string }) {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from('reviews')
        .select(`
          id, rating, comment, created_at,
          profiles!user_id(full_name),
          coach_bookings:coach_booking_id(id, lesson_type, environment, scheduled_at)
        `)
        .eq('coach_id', coachId)
        .order('created_at', { ascending: false });
      setReviews(((data ?? []) as unknown as Review[]) || []);
      setLoading(false);
    })();
  }, [coachId]);

  const count = reviews.length;
  const avg = count > 0 ? reviews.reduce((s, r) => s + r.rating, 0) / count : null;
  const dist = [0, 0, 0, 0, 0];
  reviews.forEach((r) => { if (r.rating >= 1 && r.rating <= 5) dist[r.rating - 1]++; });

  return (
    <div className="p-8 max-w-4xl">
      <PageHeader
        eyebrow="Coach"
        title="Reviews"
        subtitle="What your students have said about their lessons."
      />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        <StatCard icon={Star} label="Average rating"
          value={avg === null ? '—' : avg.toFixed(2)} />
        <StatCard icon={MessageSquare} label="Reviews" value={count} />
        <StatCard icon={Star} label="5-star share"
          value={count === 0 ? '—' : `${Math.round((dist[4] / count) * 100)}%`} />
      </div>

      <Section title="All reviews" className="p-0 overflow-hidden">
        {loading ? (
          <div className="p-8 text-sm text-gray-500 text-center">Loading…</div>
        ) : reviews.length === 0 ? (
          <EmptyState
            icon={MessageSquare}
            title="No reviews yet"
            description="Students can leave a review after a completed lesson. Once they do, it'll show up here."
          />
        ) : (
          <ul className="divide-y divide-gray-100">
            {reviews.map((r) => (
              <li key={r.id} className="p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <Stars n={r.rating} />
                      <span className="text-sm font-medium text-gray-900">
                        {r.profiles?.full_name ?? 'AceRent player'}
                      </span>
                      <span className="text-xs text-gray-400">
                        · {new Date(r.created_at).toLocaleDateString('en-GB', {
                          day: '2-digit', month: 'short', year: 'numeric',
                        })}
                      </span>
                    </div>
                    {r.coach_bookings && (
                      <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-2 flex-wrap">
                        <Badge tone="lime">
                          {r.coach_bookings.lesson_type.replace('_', ' ')} · {r.coach_bookings.environment}
                        </Badge>
                        <span>
                          Lesson on {new Date(r.coach_bookings.scheduled_at).toLocaleDateString('en-GB', {
                            day: '2-digit', month: 'short', year: 'numeric',
                          })}
                        </span>
                      </div>
                    )}
                    {r.comment && (
                      <p className="text-sm text-gray-800 mt-2 leading-relaxed whitespace-pre-line">{r.comment}</p>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}

function Stars({ n }: { n: number }) {
  return (
    <span className="inline-flex gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} className={`w-4 h-4 ${
          i <= n ? 'fill-[#c6f806] text-[#c6f806]' : 'text-gray-300'
        }`} />
      ))}
    </span>
  );
}
